/**
 * IndexedDB-based storage for pending files that need to survive page reloads.
 *
 * Unlike in-memory Zustand stores, files stored here persist across OAuth
 * redirects and page navigations. This enables the "upload → sign in → upload"
 * flow where users select a file before signing in.
 */

const DB_NAME = "bandwith-pending-files";
const STORE_NAME = "files";
const PENDING_FILE_KEY = "pending-upload";
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

/**
 * Store a file in IndexedDB. The file will persist across page reloads.
 */
export async function storePendingFile(file: File): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(file, PENDING_FILE_KEY);

    request.onerror = () => reject(request.error);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
  });
}

/**
 * Retrieve and remove the pending file from IndexedDB.
 * Returns null if no file is stored.
 */
export async function consumePendingFile(): Promise<File | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    const getRequest = store.get(PENDING_FILE_KEY);

    getRequest.onerror = () => reject(getRequest.error);
    getRequest.onsuccess = () => {
      const file = getRequest.result;
      if (file) {
        store.delete(PENDING_FILE_KEY);
      }
      tx.oncomplete = () => {
        db.close();
        resolve(file ?? null);
      };
    };
  });
}
